// IE 2026 11 03 / ODOO-349799 (clipboard target)
// Приёмник «Буфер обміну» обработки «Compare & merge» (заказ Игоря 03.11: «Почему бы в приёмнике тоже не добавить буфер обмена?»).
// Бэк (predefined_exchange.clipboard_command) отдаёт тот же пакет, что и приёмнику «Файл» (data URL архива CHIP-Records, как dounloadFile),
// командой copyToClipboard: { file_dounloadToFrontend, fileName_dounloadToFrontend, records, max_bytes }. Фронт декодирует текст, проверяет размер
// и копирует: navigator.clipboard.writeText, запасной путь — скрытое textarea + document.execCommand('copy') (нет API, нет разрешения, не https).
// Чистые функции (тест tests/unit/clipboardCopy.test.mjs): окружение (navigator / document) передаётся аргументом.

export const CLIPBOARD_MAX_BYTES = 5 * 1024 * 1024   // IE 2026 11 03 / ODOO-349799 (clipboard target): = CLIPBOARD_MAX_MB бэка; команда несёт свой max_bytes (0 - без потолка)

// data URL «data:application/json;base64,<тело>» (или «data:…,<текст>») → текст UTF-8; не data URL — как есть
export function dataUrl_text(url) {   // IE 2026 11 03 / ODOO-349799 (clipboard target)
    const s = String(url ?? '')
    if (!s.startsWith('data:')) return s
    const comma = s.indexOf(',')
    if (comma < 0) return ''
    const head = s.slice(5, comma), body = s.slice(comma + 1)
    if (!/;base64$/i.test(head)) return decodeURIComponent(body)
    const bin = typeof atob === 'function' ? atob(body) : Buffer.from(body, 'base64').toString('binary')
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    return new TextDecoder('utf-8').decode(bytes)
}

// размер текста в байтах UTF-8
export function text_bytes(text) {   // IE 2026 11 03 / ODOO-349799 (clipboard target)
    return new TextEncoder().encode(String(text ?? '')).length
}

// «12 KB» / «1.4 MB» для тоста
export function size_label(bytes) {   // IE 2026 11 03 / ODOO-349799 (clipboard target)
    const n = Number(bytes) || 0
    if (n >= 1024 * 1024) return `${(n / 1048576).toFixed(1)} MB`
    return `${Math.max(1, Math.round(n / 1024))} KB`
}

// команда бэка → { ok, text, bytes, records, size } или { ok: false, reason: 'empty' | 'too_big', bytes, limit }
export function clipboardPayload(action, maxBytes) {   // IE 2026 11 03 / ODOO-349799 (clipboard target)
    const text = action?.text !== undefined ? String(action.text) : dataUrl_text(action?.file_dounloadToFrontend)
    const bytes = text_bytes(text)
    const limit = maxBytes !== undefined ? Number(maxBytes) : (action?.max_bytes !== undefined ? Number(action.max_bytes) : CLIPBOARD_MAX_BYTES)
    if (!text) return { ok: false, reason: 'empty', bytes: 0, limit }
    if (limit > 0 && bytes > limit) return { ok: false, reason: 'too_big', bytes, limit }
    let records = Number(action?.records)
    if (!Number.isFinite(records) || records < 0) {
        try { const d = JSON.parse(text); records = Array.isArray(d?.doc?.data) ? d.doc.data.length : Array.isArray(d) ? d.length : 1 } catch (e) { records = 0 }
    }
    return { ok: true, text, bytes, records, size: size_label(bytes) }
}

// запасной путь: скрытое textarea + execCommand('copy') → true / false
export function copy_viaTextarea(text, doc) {   // IE 2026 11 03 / ODOO-349799 (clipboard target)
    if (!doc || typeof doc.createElement !== 'function' || typeof doc.execCommand !== 'function') return false
    const ta = doc.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'; ta.style.top = '-1000px'; ta.style.left = '-1000px'; ta.style.opacity = '0'
    const parent = doc.body || doc.documentElement
    parent.appendChild(ta)
    let ok = false
    try {
        ta.focus?.(); ta.select?.()
        ok = !!doc.execCommand('copy')
    } catch (e) {
        ok = false
    } finally {
        parent.removeChild(ta)
    }
    return ok
}

// копирование: API буфера, при отказе/отсутствии — textarea → { ok, via: 'clipboard' | 'textarea' | '', error }
export async function copyText(text, env = {}) {   // IE 2026 11 03 / ODOO-349799 (clipboard target)
    const nav = env.navigator, doc = env.document
    let error = ''
    if (nav?.clipboard && typeof nav.clipboard.writeText === 'function') {
        try {
            await nav.clipboard.writeText(text)
            return { ok: true, via: 'clipboard', error: '' }
        } catch (e) {
            error = String(e?.message || e || '')
        }
    }
    if (copy_viaTextarea(text, doc)) return { ok: true, via: 'textarea', error: '' }
    return { ok: false, via: '', error: error || 'clipboard is not available' }
}

// вся команда: размер, копирование, текст тоста. t — функция перевода (t(key, params)) → { ok, status, text }
export async function clipboardCommand(action, env = {}, t = (k, p) => k.replace(/\{(\w+)\}/g, (_m, n) => p?.[n] ?? '')) {   // IE 2026 11 03 / ODOO-349799 (clipboard target)
    const p = clipboardPayload(action)
    if (!p.ok && p.reason === 'too_big') {
        return { ok: false, status: 'error', text: t('The package is {size} - above the clipboard limit of {limit}: choose the target File', { size: size_label(p.bytes), limit: size_label(p.limit) }) }
    }
    if (!p.ok) return { ok: false, status: 'error', text: t('Nothing to copy to the clipboard') }
    const r = await copyText(p.text, env)
    if (!r.ok) return { ok: false, status: 'error', text: t('Could not copy to the clipboard (the browser refused: {error}): choose the target File', { error: r.error }) }
    return { ok: true, status: 'success', via: r.via, text: t('Copied to clipboard: {n} records / {size}', { n: p.records, size: p.size }) }
}
